import {Controller} from "@hotwired/stimulus"

/**
 * Handles two contexts:
 *  1. Main page (inside the edit form) — role "page"
 *     Targets: selected, counter. Handles: openDrawer, remove, handleApply (via event).
 *  2. Drawer (loaded via global-drawer turbo-frame) — role "drawer"
 *     Targets: list, counter, search. Handles: toggle, filter, applyAndClose.
 */
export default class extends Controller {
    static targets = ["list", "selected", "counter", "search"]
  /** Fallback only; ERB sets data-interests-max-value from Interest::MAX_PER_USER. */
    static values = {
        max: {type: Number, default: 3},
        url: {type: String, default: ""}
    }

  // ── Lifecycle ──

    connect() {
        if (this.hasSelectedTarget) {
            this.boundHandleApply = this.handleApply.bind(this)
            document.addEventListener("interests:apply", this.boundHandleApply)
        }
    }

    disconnect() {
        if (this.boundHandleApply) {
            document.removeEventListener("interests:apply", this.boundHandleApply)
        }
    }

  // ── Page actions ──

  get currentCodes() {
    const form = this.element.closest("form")
    if (!form) return []
    return [...form.querySelectorAll('input[name="interests[]"][data-managed]')]
        .map(i => i.value)
        .filter(Boolean)
  }

  get drawerSelectedCount() {
    if (!this.hasListTarget) return 0
    return this.listTarget.querySelectorAll('[data-selected="true"]').length
  }

  // ── Drawer actions ──

    toggle(event) {
        const btn = event.currentTarget
        const isSelected = btn.dataset.selected === "true"

        if (!isSelected && this.drawerSelectedCount >= this.maxValue) return

        btn.dataset.selected = (!isSelected).toString()
        this.refreshDrawer()
    }

    filter(event) {
        const query = event.target.value.toLowerCase().trim()
        this.listTarget.querySelectorAll("[data-interest-code]").forEach(el => {
            const name = el.querySelector("[data-name]")?.textContent.toLowerCase() || ""
            el.style.display = name.includes(query) ? "" : "none"
        })
    }

    applyAndClose() {
        const codes = [...this.listTarget.querySelectorAll('[data-selected="true"]')]
            .map(el => ({
                code: el.dataset.interestCode,
                name: el.querySelector("[data-name]")?.textContent.trim() || el.dataset.interestCode
            }))

        document.dispatchEvent(new CustomEvent("interests:apply", {detail: {interests: codes}}))
        document.dispatchEvent(new CustomEvent("global-drawer:close"))
    }

  // ── Page helpers ──

    handleApply(event) {
        const {interests} = event.detail
        const form = this.element.closest("form")
        if (!form) return

        form.querySelectorAll('input[name="interests[]"][data-managed]').forEach(el => el.remove())

        if (interests.length === 0) {
            this.appendHiddenField(form, "")
        } else {
            interests.forEach(({code}) => this.appendHiddenField(form, code))
        }

        this.renderChips(interests)
        this.updatePageCounter()
    }

    renderChips(interests) {
        if (!this.hasSelectedTarget) return

        this.selectedTarget.innerHTML = ""

        if (interests.length === 0) {
            this.selectedTarget.innerHTML = `<p class="text-sm text-gray-400 dark:text-neutral-500 italic">${this.selectedTarget.dataset.emptyText || ""}</p>`
            return
        }

        interests.forEach(({code, name}) => {
            const chip = document.createElement("div")
            chip.className = "flex items-center gap-1.5 px-3 py-1.5 bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 rounded-full text-sm font-medium"
            chip.dataset.interestCode = code
            chip.innerHTML = `
        <span>${name}</span>
        <button type="button"
                class="shrink-0 size-4 flex items-center justify-center rounded-full hover:bg-sky-200 dark:hover:bg-sky-800 transition-colors"
                data-action="click->interests#remove"
                data-interest-code="${code}">
          <svg class="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      `
            this.selectedTarget.appendChild(chip)
        })
    }

    updatePageCounter() {
        if (!this.hasCounterTarget) return
        const count = this.hasSelectedTarget
            ? this.selectedTarget.querySelectorAll("[data-interest-code]").length
            : 0
        this.counterTarget.textContent = `${count} / ${this.maxValue}`
    }

    removeHiddenField(code) {
        const form = this.element.closest("form")
        if (!form) return
        const field = form.querySelector(`input[name="interests[]"][data-managed][value="${code}"]`)
        if (field) field.remove()
    }

    ensureBlankField() {
        const form = this.element.closest("form")
        if (!form) return
        const remaining = form.querySelectorAll('input[name="interests[]"][data-managed]')
        if (remaining.length === 0) {
            this.appendHiddenField(form, "")
        }
    }

    appendHiddenField(form, value) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "interests[]"
        input.value = value
        input.dataset.managed = "true"
        form.appendChild(input)
    }

  openDrawer() {
    const codes = this.currentCodes
    const url = new URL(this.urlValue, window.location.origin)
    codes.forEach(c => url.searchParams.append("selected[]", c))

    document.dispatchEvent(new CustomEvent("global-drawer:open", {
      detail: {src: url.toString()}
    }))
  }

  // ── Drawer helpers ──

  remove(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.interestCode

    const chip = this.selectedTarget.querySelector(`[data-interest-code="${code}"]`)
    if (chip) chip.remove()

    this.removeHiddenField(code)
    this.ensureBlankField()

    if (this.selectedTarget.querySelectorAll("[data-interest-code]").length === 0) {
      this.selectedTarget.innerHTML = `<p class="text-sm text-gray-400 dark:text-neutral-500 italic">${this.selectedTarget.dataset.emptyText || ""}</p>`
    }

    this.updatePageCounter()
  }

  refreshDrawer() {
    const count = this.drawerSelectedCount
    const atLimit = count >= this.maxValue

    this.listTarget.querySelectorAll("[data-interest-code]").forEach(el => {
      const isSelected = el.dataset.selected === "true"
      const check = el.querySelector("[data-check]")

      if (isSelected) {
        el.classList.add("bg-sky-100", "border-sky-400", "dark:bg-sky-900/40", "dark:border-sky-500/50")
        el.classList.remove("bg-white", "border-gray-200", "dark:bg-neutral-800", "dark:border-neutral-700", "opacity-50", "pointer-events-none")
        if (check) check.classList.remove("hidden")
      } else {
        el.classList.remove("bg-sky-100", "border-sky-400", "dark:bg-sky-900/40", "dark:border-sky-500/50")
        el.classList.add("bg-white", "border-gray-200", "dark:bg-neutral-800", "dark:border-neutral-700")
        if (check) check.classList.add("hidden")

        if (atLimit) {
          el.classList.add("opacity-50", "pointer-events-none")
        } else {
          el.classList.remove("opacity-50", "pointer-events-none")
        }
      }
    })

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} / ${this.maxValue}`
    }
  }
}
